import { supabase } from "@/lib/supabase";

const BUCKET = "concierge-media";

export const ALLOWED_VIDEO_MIME = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
] as const;
export const MAX_VIDEO_BYTES = 500 * 1024 * 1024;

export function conciergeMediaPath(orgId: string, siteId: string, resourceId: string, filename: string) {
  const safe = filename.replace(/[^a-zA-Z0-9._-]+/g, "_");
  return `${orgId}/${siteId}/${resourceId}/${safe}`;
}

export async function uploadConciergeVideo(
  file: File,
  args: { orgId: string; siteId: string; resourceId: string },
): Promise<{ path: string; mime_type: string; size_bytes: number; duration_ms: number | null }> {
  if (!ALLOWED_VIDEO_MIME.includes(file.type as any)) {
    throw new Error("Unsupported video type. Use MP4, WebM, or MOV.");
  }
  if (file.size > MAX_VIDEO_BYTES) {
    throw new Error("Video too large (max 500 MB).");
  }

  const path = conciergeMediaPath(args.orgId, args.siteId, args.resourceId, file.name);
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type,
    upsert: false,
  });
  if (error) throw error;

  const duration_ms = await readVideoDurationMs(file).catch(() => null);
  return {
    path,
    mime_type: file.type,
    size_bytes: file.size,
    duration_ms,
  };
}

export async function getConciergeMediaSignedUrl(path: string, expiresInSec = 3600): Promise<string | null> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, expiresInSec);
  if (error) {
    console.warn("[concierge-media] signed url error", error);
    return null;
  }
  return data?.signedUrl ?? null;
}

function readVideoDurationMs(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      const ms = isFinite(v.duration) ? Math.round(v.duration * 1000) : null;
      URL.revokeObjectURL(url);
      resolve(ms);
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    v.src = url;
  });
}
